import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';
import { fieldErrors } from '../../../shared/api.js';
import { PlatformModal, Field } from './PlatformModal.jsx';
import { cleanSlugInput, slugError, slugify } from '../slug.js';
import { useCreateCompany } from '../usePlatformCompanies.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const USERNAME_RE = /^[a-zA-Z0-9._-]{3,32}$/;

export function CreateCompanyModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [ownerFullName, setOwnerFullName] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerUsername, setOwnerUsername] = useState('admin');
  const [notes, setNotes] = useState('');
  const [activate, setActivate] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [takenSlug, setTakenSlug] = useState('');

  const create = useCreateCompany({
    onSuccess: (res) => onCreated(res),
    onError: (err) => {
      setServerErrors(fieldErrors(err));
      if (err?.response?.status === 409) setTakenSlug(slug);
    }
  });

  const errors = useMemo(() => {
    const e = {};
    if (!name.trim()) e.name = 'اسم الشركة مطلوب';
    else if (name.trim().length < 2) e.name = 'اسم الشركة قصير جداً';
    const se = slugError(slug);
    if (se) e.slug = se;
    else if (takenSlug && takenSlug === slug) e.slug = 'هذا الكود محجوز لشركة أخرى — اختر كوداً مختلفاً';
    if (!ownerFullName.trim()) e.ownerFullName = 'اسم المالك مطلوب';
    if (!ownerUsername.trim()) e.ownerUsername = 'اسم مستخدم المالك مطلوب';
    else if (!USERNAME_RE.test(ownerUsername.trim())) {
      e.ownerUsername = 'حروف إنجليزية وأرقام و( . _ - ) فقط، 3 أحرف على الأقل';
    }
    if (ownerEmail.trim() && !EMAIL_RE.test(ownerEmail.trim())) e.ownerEmail = 'بريد إلكتروني غير صحيح';
    return e;
  }, [name, slug, ownerFullName, ownerUsername, ownerEmail, takenSlug]);

  const show = (key) => (submitted ? errors[key] : undefined) || serverErrors[key];
  const valid = Object.keys(errors).length === 0;

  function onNameChange(value) {
    setName(value);
    if (!slugEdited) setSlug(slugify(value));
    setTakenSlug('');
  }

  function onSlugChange(value) {
    setSlugEdited(true);
    setSlug(cleanSlugInput(value));
    setTakenSlug('');
  }

  function submit() {
    setSubmitted(true);
    setServerErrors({});
    if (!valid || create.isPending) return;
    const body = {
      name: name.trim(),
      slug,
      ownerFullName: ownerFullName.trim(),
      ownerUsername: ownerUsername.trim(),
      activate
    };
    if (ownerPhone.trim()) body.ownerPhone = ownerPhone.trim();
    if (ownerEmail.trim()) body.ownerEmail = ownerEmail.trim();
    if (notes.trim()) body.notes = notes.trim();
    create.mutate(body);
  }

  return (
    <PlatformModal
      title="شركة جديدة"
      sub="تُنشأ الشركة وقاعدة بياناتها الخاصة، ويُنشأ حساب المالك داخلها"
      onClose={onClose}
      width={560}
      footer={(
        <>
          <button type="button" className="btn flex-1 justify-center" onClick={onClose}>إلغاء</button>
          <button
            type="button"
            className="btn btn-primary flex-1 justify-center"
            onClick={submit}
            disabled={create.isPending}
          >
            <Icon name="check" size={14} />
            {create.isPending ? 'جاري الإنشاء...' : activate ? 'إنشاء وتفعيل' : 'إنشاء الشركة'}
          </button>
        </>
      )}
    >
      <Field label="اسم الشركة" required error={show('name')} hint="الاسم التجاري كما يظهر داخل النظام">
        <input
          className={clsx('input', show('name') && 'border-red')}
          value={name}
          autoFocus
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="مثال: مدينة ألعاب المرح"
        />
      </Field>

      <Field
        label="كود الشركة (slug)"
        required
        error={show('slug')}
        hint="يكتبه موظفو الشركة في شاشة الدخول — حروف إنجليزية صغيرة وأرقام وشرطة، من 2 إلى 32 حرفاً."
      >
        <input
          className={clsx('input font-mono', show('slug') && 'border-red')}
          value={slug}
          dir="ltr"
          style={{ textAlign: 'left' }}
          onChange={(e) => onSlugChange(e.target.value)}
          onBlur={() => setSlug((v) => v.replace(/^-+|-+$/g, ''))}
          placeholder="marah-land"
        />
      </Field>

      <div className="rounded-[10px] p-2.5 text-[12px]" style={{ background: '#FFF7ED', color: '#9A3412' }}>
        كود الشركة ثابت نهائياً بعد الإنشاء ولا يمكن تغييره — تأكّد منه قبل الحفظ.
      </div>

      <div className="border-t border-line pt-3 space-y-3">
        <div className="text-[12.5px] font-bold">حساب مالك الشركة</div>

        <Field label="الاسم الكامل" required error={show('ownerFullName')}>
          <input
            className={clsx('input', show('ownerFullName') && 'border-red')}
            value={ownerFullName}
            onChange={(e) => setOwnerFullName(e.target.value)}
            placeholder="مثال: مصطفى المرح"
          />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="الهاتف" error={show('ownerPhone')}>
            <input
              className={clsx('input', show('ownerPhone') && 'border-red')}
              value={ownerPhone}
              dir="ltr"
              style={{ textAlign: 'left' }}
              onChange={(e) => setOwnerPhone(e.target.value)}
              placeholder="07XXXXXXXXX"
            />
          </Field>
          <Field label="البريد الإلكتروني" error={show('ownerEmail')}>
            <input
              className={clsx('input', show('ownerEmail') && 'border-red')}
              value={ownerEmail}
              dir="ltr"
              style={{ textAlign: 'left' }}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="owner@example.com"
            />
          </Field>
        </div>

        <Field
          label="اسم المستخدم"
          required
          error={show('ownerUsername')}
          hint="يُستخدم للدخول مع كود الشركة — الافتراضي admin"
        >
          <input
            className={clsx('input font-mono', show('ownerUsername') && 'border-red')}
            value={ownerUsername}
            dir="ltr"
            style={{ textAlign: 'left' }}
            onChange={(e) => setOwnerUsername(e.target.value)}
            placeholder="admin"
          />
        </Field>
      </div>

      <Field label="ملاحظات (اختياري)" error={show('notes')}>
        <textarea
          className="input"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="أي تفاصيل إدارية عن الشركة…"
        />
      </Field>

      <label className="flex items-start gap-2 cursor-pointer rounded-[10px] border border-line p-2.5">
        <input
          type="checkbox"
          className="accent-orange mt-0.5"
          checked={activate}
          onChange={(e) => setActivate(e.target.checked)}
        />
        <span className="text-[12.5px]">
          <b>تفعيل فوراً</b>
          <div className="text-ink-3 text-[11.5px] mt-0.5">
            تُهيّأ قاعدة بيانات الشركة ويُنشأ حساب المالك الآن، وستظهر كلمة المرور مرّة واحدة فقط.
            بدون التفعيل تبقى الشركة «بانتظار الموافقة».
          </div>
        </span>
      </label>
    </PlatformModal>
  );
}
